import React, { useState, useEffect } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import toast from "react-hot-toast";

//Service
import ItemService from "../../../services/supplier_appCommonService";

//Components
import Layout from "../../../Layout/Layout";
import { SearchField } from "../../../Components/formElements/SearchField";
import { TableStatusField } from "../../../Components/formElements/TableStatusField";
import SupplierConstants from "../../../common/SupplierConstants";

const AdvancePaymentList = () => {
  const navigate = useNavigate();
  localStorage.setItem("menu", "Finance");
  localStorage.setItem("submenu", "Payment");

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [isCardVisible, setCardVisible] = useState(true);
  const [activePaymentType, setActivePaymentType] = useState("Pay");

  // Fetch Advance Payment List
  const fetchAdvancePaymentList = async (pageNum = 1, perPageNum = 10, search = "", paymentType = "Pay") => {
    try {
      setLoading(true);
      const response = await ItemService.generateQuery({
        queryName: "getAdvancePaymentList",
        page: pageNum,
        per_page: 1000,
        searchTerm: search,
      });

      if (response?.status === 200) {
        const allData = response.data || [];
        // Client-side filter by payment type
        const filtered = allData.filter(
          (item) => item.paymentType === paymentType
        );
        // Client-side search filter
        const searchLower = search.toLowerCase();
        const searched = search
          ? filtered.filter(
              (item) =>
                (item.documentNumber || "").toLowerCase().includes(searchLower) ||
                (item.partyName || "").toLowerCase().includes(searchLower) ||
                (item.party || "").toLowerCase().includes(searchLower)
            )
          : filtered;
        // Client-side pagination
        const startIndex = (pageNum - 1) * perPageNum;
        const paginatedData = searched.slice(startIndex, startIndex + perPageNum);
        setData(paginatedData);
        setTotalRows(searched.length);
      } else {
        setData([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error("Error fetching advance payment list:", error);
      toast.error("Failed to load Advance Payment List");
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchAdvancePaymentList(page, perPage, searchText, activePaymentType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle page change
  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchAdvancePaymentList(newPage, perPage, searchText, activePaymentType);
  };

  // Handle rows per page change
  const handlePerRowsChange = (newPerPage, newPage) => {
    setPerPage(newPerPage);
    setPage(newPage);
    fetchAdvancePaymentList(newPage, newPerPage, searchText, activePaymentType);
  };

  // Handle search
  const handleSearch = () => {
    setPage(1);
    fetchAdvancePaymentList(1, perPage, searchText, activePaymentType);
  };

  // Handle Pay/Receive filter toggle
  const handlePaymentTypeFilter = (type) => {
    setActivePaymentType(type);
    setPage(1);
    fetchAdvancePaymentList(1, perPage, searchText, type);
  };

  // Handle search on Enter key
  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Navigate to detail page
  const navigateToDetail = (documentNumber) => {
    navigate(SupplierConstants.SUPPLIER_FINANCE_ADVANCE_PAYMENT_DETAILS, {
      state: { documentNumber },
    });
  };

  const columns = [
    {
      name: "Document No",
      cell: (row) => (
        <button
          className="btnInfo"
          onClick={() => navigateToDetail(row.documentNumber || row.advancePaymentRequestNr)}
        >
          {row.documentNumber || row.advancePaymentRequestNr || "--"}
        </button>
      ),
      sortable: true,
      width: "150px",
    },
    {
      name: "Payment Type",
      selector: (row) => row.paymentType || "--",
      sortable: true,
      width: "130px",
    },
    {
      name: "Party Type",
      selector: (row) => row.partyType || "--",
      sortable: true,
      width: "120px",
    },
    {
      name: "Party Name",
      selector: (row) => row.partyName || "--",
      sortable: true,
      width: "180px",
    },
    {
      name: "Party",
      selector: (row) => row.party || "--",
      sortable: true,
      width: "180px",
    },
    {
      name: "Paid Amount",
      cell: (row) => (
        <span className="fw-bold">
          {row.paidAmount ? `₹ ${Number(row.paidAmount).toLocaleString("en-IN")}` : "--"}
        </span>
      ),
      sortable: true,
      width: "140px",
    },
    {
      name: "Total Allocated",
      selector: (row) => row.totalAllocatedAmount || "--",
      sortable: true,
      width: "140px",
    },
    {
      name: "Difference",
      selector: (row) => row.differenceAmount || "--",
      sortable: true,
      width: "130px",
    },
    {
      name: "Posting Date",
      selector: (row) => row.postingDate ? new Date(row.postingDate).toLocaleDateString("en-IN") : "--",
      sortable: true,
      width: "120px",
    },
    {
      name: "Cheque/Ref No",
      selector: (row) => row.chequeRefNo || "--",
      sortable: true,
      width: "130px",
    },
    {
      name: "Status",
      selector: (row) => row.status || "Active",
      cell: (row) => <TableStatusField status={row.status || "Active"} />,
      width: "120px",
    },
    {
      name: "Created On",
      selector: (row) => row.createdAt ? new Date(row.createdAt).toLocaleDateString("en-IN") : "--",
      sortable: true,
      width: "120px",
    },
  ];

  const toggleCardVisibility = () => {
    setCardVisible(!isCardVisible);
  };

  return (
    <main className="dashboard main" id="main">
      <Layout />
      <Row className="justify-center subContent">
        <Col xs={12} md={12} className="rightSide">
          <div className="rightSideInner PageCommonCSS">
            <Row>
              <Col xs={12} md={9} lg={9}>
                <h2 className="mainTitle">Advance Payment List</h2>
                <p className="subText">
                  Lists of Advance Payment Requests
                </p>
              </Col>
              <Col xs={12} md={3} lg={3} className="mt-3">
                <SearchField
                  name="searchKey"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onClick={handleSearch}
                  onKeyPress={handleSearchKeyPress}
                  placeholder="Search by Document No, Party Name..."
                />
              </Col>
            </Row>

            <hr />

            <div className="accordion" id="accordionExample">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <Row className="accordion-button">
                    <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                      <i
                        className={`bi ${
                          isCardVisible ? "bi-chevron-right" : "bi-chevron-down"
                        }`}
                        onClick={toggleCardVisibility}
                      ></i>
                    </Col>
                    <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                      <span className="iconLogo">AP</span>
                    </Col>
                    <Col xs={12} md={6} lg={6}>
                      <span className="headerTitle">Advance Payment List</span>
                      <span className="subHeaderTitle">
                        View all Advance Payment Requests
                      </span>
                    </Col>
                    <Col xs={1} md={1} lg={1} className="d-flex justify-content-end">
                      <button
                        className={`btnTable ${activePaymentType === "Pay" ? "active" : ""}`}
                        onClick={() => handlePaymentTypeFilter("Pay")}
                      >
                        Pay
                      </button>
                    </Col>
                    <Col xs={1} md={1} lg={1} className="d-flex justify-content-end">
                      <button
                        className={`btnTable ${activePaymentType === "Receive" ? "active" : ""}`}
                        onClick={() => handlePaymentTypeFilter("Receive")}
                      >
                        Receive
                      </button>
                    </Col>
                    <Col xs={2} md={2} lg={2} className="d-flex justify-content-end">
                      <button
                        className="btnTable text-nowrap"
                        onClick={() => navigate(SupplierConstants.SUPPLIER_FINANCE_ADVANCE_PAYMENT_CREATE)}
                        title="New Advance Payment"
                      >
                        <i className="bi bi-plus-lg"></i> New Advance Payment
                      </button>
                    </Col>
                  </Row>
                </h2>

                <div
                  className={`accordion-collapse collapse show ${
                    isCardVisible ? "visible" : ""
                  }`}
                >
                  <div className="accordion-body">
                    <DataTable
                      title=""
                      columns={columns}
                      data={data}
                      progressPending={loading}
                      pagination
                      paginationServer
                      paginationTotalRows={totalRows}
                      onChangeRowsPerPage={handlePerRowsChange}
                      onChangePage={handlePageChange}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      highlightOnHover
                      striped
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </main>
  );
};

export default AdvancePaymentList;